/*!
 * hold.js — hold(): freeze a sound's patterns where they are
 * © 2026 Rob Canning — GPLv3
 *
 *     hold(s)            s's patterns stay on the values they drew last --
 *     hold(s, 1)         the note, amp, dur, wave, filter, pan -- and the
 *                        rhythm goes on: a struck synth strikes the same
 *                        note, a held one holds it
 *     hold(s, 0)         they carry on from there
 *     hold([a, b], 1)    several; hold(all, 0) every one
 *     hold(bsc)          an animation by its uid (bass > scale(uid:bsc, ...)):
 *     hold(bsc, 0)       it stops where it is, and goes on from there
 *
 * A poly sound holds every voice, and any it starts while held. hush()
 * lets go of what it stops. Synth patterns (synth.js does the holding);
 * an animation's own pause and play (its controller, cfg._anim). Ctrl+_
 * on a line holds its synths and its animations, and lets them go.
 */

import { holdSynth } from "./synth.js";
import { levelArgs } from "./audio/level.js";
import { synthNames } from "./audio/voices.js";

/** Every synth sound by name, for hold(all, ...). */
const heldAnimUids = new Set();
/** The animations hold() has stopped, by uid (the console's Ctrl+_ turns them round). */
export const heldAnims = () => [...heldAnimUids].filter(u => window.oscillaAnimRegistry?.[u]);

/** An animation stopped where it is, or going on from there. */
function holdAnim(uid, on) {
  const cfg = window.oscillaAnimRegistry?.[uid]?.cfg;
  const ra = window.runningAnimations?.get(uid);
  if (on) {
    if (ra?.pause) ra.pause(); else cfg?._anim?.pause?.();
    heldAnimUids.add(uid);
  } else {
    if (ra?.resume) ra.resume(); else if (cfg?._anim?.play) cfg._anim.play(); else cfg?._anim?.resume?.();
    heldAnimUids.delete(uid);
  }
}

/** hold(names, 1 | 0) as a cue: a sound's patterns, or an animation (its uid). */
export function handleHoldCue(ast) {
  const a = levelArgs(ast?.args);
  if (!a.names.length) { console.warn("[hold] which sound? hold(s) / hold(s, 0)"); return; }
  const on = Number(a.numbers[0] ?? 1) !== 0;
  const synths = new Set(synthNames());
  for (const n of a.names.includes("all") ? [...synths] : a.names) {
    // an animation's uid, a sound's name -- or both (shape(uid:sh1) rotate(...) synth(uid:sh1 ...))
    const anim = !!window.oscillaAnimRegistry?.[n];
    if (anim) holdAnim(n, on);
    if (!anim || synths.has(n)) holdSynth(n, on);
  }
}
